// src/utils/buildFindingTicket.js
//
// Finding-grade bug ticket generator (WP6, launch-signoff-spec.md §8). Turns
// one conformance-engine check result into copy-ready markdown: title,
// summary (expected vs got), spec citation (knowledge pack), evidence rows
// (deep-linked back into the dashboard via requestLink()), repro context, a
// replay cURL for the first resolvable failing row, and a run reference.
//
// Contract: buildFindingTicket({ check, result, app, runId, launch,
//   rowsByRequestId, suiteName }) -> markdown string. NEVER throws — every
// section tolerates missing data and renders '(not captured)' rather than
// crashing a button handler; the whole build is additionally wrapped so a
// truly unexpected error still returns a minimal ticket string.
//
//   check   — { id, description, severity?, target?, _suite? } (may be partial)
//   result  — the evaluateCheck() result shape: { checkId, status, severity,
//             matched, found, failures:[{row,got,expected}],
//             evidence:[{row,got,expected,pass}],
//             rows:[{requestType,url,id,requestId,ts,read,ok}], note?,
//             failureSamples?:[{ts,requestType,host,iu,path,got,expected,requestId}],
//             patterns? }
//   app     — selected app id/name string, optional
//   runId   — the conformance run's id, optional
//   launch  — { id, ticket, build, ... } from the launch workspace, optional
//   rowsByRequestId — Map or plain object: requestId -> full captured row
//             (url/method/requestHeaders/requestBody/device/step/...), optional
//   suiteName — fallback suite label when the check carries no knowledge
//             citation and `check._suite` isn't set, optional

import { getTicketContext, getRequirement } from '../knowledge';
import { requestLink } from './deepLinks';
import { buildCurl } from './curl';

const NOT_CAPTURED = '(not captured)';
const MAX_BODY_LEN = 10 * 1024; // 10KB — skip inlining anything larger

/** JSON.stringify + truncate, never throws. */
function safeJson(v, max = 200) {
  try {
    if (v === undefined) return 'undefined';
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    if (s == null) return 'null';
    return s.length > max ? `${s.slice(0, max)}…` : s;
  } catch {
    try { return String(v).slice(0, max); } catch { return '(unstringifiable)'; }
  }
}

function hostFromUrl(url) {
  if (!url || typeof url !== 'string') return null;
  try { return new URL(url).hostname; } catch { return null; }
}

/** requestId -> full row lookup, tolerant of Map or plain object. */
function getRow(rowsByRequestId, requestId) {
  if (!rowsByRequestId || !requestId) return null;
  try {
    if (typeof rowsByRequestId.get === 'function') return rowsByRequestId.get(requestId) || null;
    return rowsByRequestId[requestId] || null;
  } catch {
    return null;
  }
}

// ── tolerant field readers over the three possible evidence-entry shapes:
//    result.rows[]           {requestType,url,id,requestId,ts,read,ok}
//    result.failureSamples[] {ts,requestType,host,iu,path,got,expected,requestId}
//    result.evidence[]       {row,got,expected,pass} — row is the full session
//                             row (may be null for aggregate checks)
function entryRequestId(e) {
  return e?.requestId ?? e?.row?.requestId ?? null;
}
function entryRequestType(e) {
  return e?.requestType ?? e?.row?.requestType ?? null;
}
function entryUrlOrPath(e) {
  return e?.url || e?.path || e?.row?.url || null;
}
function entryHost(e) {
  return e?.host || hostFromUrl(entryUrlOrPath(e)) || null;
}
function entryRead(e) {
  if (e?.read != null) return e.read;
  if (e?.got !== undefined) return safeJson(e.got, 120);
  return null;
}
/** The full captured row for an evidence entry: prefer a rowsByRequestId
 * lookup (the entry's own shapes — result.rows[]/failureSamples[] — don't
 * carry `step`/`device`), falling back to evidence[]'s nested `.row`. */
function fullRowFor(e, rowsByRequestId) {
  return getRow(rowsByRequestId, entryRequestId(e)) || e?.row || null;
}
function entryStep(e, rowsByRequestId) {
  const full = fullRowFor(e, rowsByRequestId);
  return e?.step ?? full?.step ?? null;
}
function entryTs(e) {
  return e?.ts ?? e?.row?.ts ?? e?.row?.timestamp ?? e?.row?.startedAt ?? null;
}
function tsToMillis(ts) {
  if (ts == null) return NaN;
  if (typeof ts === 'number') return ts;
  const n = Date.parse(ts);
  return Number.isFinite(n) ? n : NaN;
}

// ── section builders ────────────────────────────────────────────────────

function buildTitle(check, result) {
  const sev = check?.severity || result?.status || 'info';
  const desc = check?.description || check?.id || result?.checkId || '(unnamed check)';
  return `### [${sev}] ${desc}`;
}

function buildSummary(result) {
  const lines = ['**Summary**', ''];
  lines.push(`- Status: ${result?.status || NOT_CAPTURED}`);
  const matched = typeof result?.matched === 'number' ? result.matched : null;
  const found = typeof result?.found === 'number' ? result.found : null;
  lines.push(
    `- Matched: ${matched !== null ? matched : NOT_CAPTURED}`
    + ` · Found: ${found !== null ? found : NOT_CAPTURED}`
  );
  const failures = Array.isArray(result?.failures) ? result.failures : [];
  if (failures.length) {
    failures.slice(0, 3).forEach((f) => {
      lines.push(`- Expected ${safeJson(f?.expected)} · Got ${safeJson(f?.got)}`);
    });
  } else {
    lines.push(`- Expected vs got: ${NOT_CAPTURED}`);
  }
  return lines.join('\n');
}

function buildSpecReference(check, result, suiteName) {
  const lines = ['**Spec reference**', ''];
  const checkId = check?.id || result?.checkId || null;

  let ctx = null;
  let req = null;
  try { ctx = checkId ? getTicketContext(checkId) : null; } catch { ctx = null; }
  try { req = checkId ? getRequirement(checkId) : null; } catch { req = null; }

  const reference = req?.reference || ctx?.reference || null;
  const sourceUrl = req?._sourceUrl || null;
  const source = req?._source || ctx?.source || null;

  if (reference) {
    lines.push(`- ${reference}${source ? ` (${source})` : ''}`);
    lines.push(`- ${sourceUrl || NOT_CAPTURED}`);
  } else {
    const suite = suiteName || check?._suite || null;
    const desc = check?.description || checkId || NOT_CAPTURED;
    lines.push(`- ${desc}${suite ? ` (suite: ${suite})` : ''}`);
  }
  return lines.join('\n');
}

/** ≤5 entries, preferring failing rows: result.rows (!ok) > failureSamples > evidence. */
function pickEvidenceEntries(result) {
  const rows = Array.isArray(result?.rows) ? result.rows.filter((r) => r && r.ok === false) : [];
  if (rows.length) return rows.slice(0, 5);
  const samples = Array.isArray(result?.failureSamples) ? result.failureSamples : [];
  if (samples.length) return samples.slice(0, 5);
  const evidence = Array.isArray(result?.evidence) ? result.evidence : [];
  return evidence.slice(0, 5);
}

function buildEvidence(result) {
  const entries = pickEvidenceEntries(result);
  const lines = ['**Evidence**', ''];
  if (!entries.length) {
    lines.push(`- ${NOT_CAPTURED}`);
    return { text: lines.join('\n'), entries };
  }
  entries.forEach((e) => {
    const type = entryRequestType(e) || '(unknown type)';
    const hostOrUrl = (entryHost(e) || entryUrlOrPath(e) || '(no host)').slice(0, 80);
    const read = entryRead(e) ?? NOT_CAPTURED;
    const rid = entryRequestId(e);
    let link = '';
    try {
      const l = rid ? requestLink(rid) : null;
      if (l) link = ` · [open](${l})`;
    } catch { link = ''; }
    lines.push(`- ${type} · ${hostOrUrl} · read: ${read}${link}`);
  });
  return { text: lines.join('\n'), entries };
}

function buildReproContext(app, launch, rowsByRequestId, entries) {
  const lines = ['**Repro context**', ''];
  lines.push(`- App: ${app || NOT_CAPTURED}`);
  lines.push(`- Build: ${launch?.build || NOT_CAPTURED}`);

  const steps = Array.from(new Set(entries.map((e) => entryStep(e, rowsByRequestId)).filter(Boolean)));
  lines.push(`- Step(s): ${steps.length ? steps.join(', ') : NOT_CAPTURED}`);

  let device = NOT_CAPTURED;
  try {
    for (const e of entries) {
      const full = fullRowFor(e, rowsByRequestId);
      if (full?.device) {
        device = typeof full.device === 'string' ? full.device : safeJson(full.device, 200);
        break;
      }
    }
  } catch { /* keep default */ }
  lines.push(`- Device: ${device}`);

  const millis = entries.map(entryTs).map(tsToMillis).filter(Number.isFinite);
  if (millis.length) {
    const fmt = (n) => { try { return new Date(n).toLocaleString(); } catch { return String(n); } };
    lines.push(`- Capture window: ${fmt(Math.min(...millis))} → ${fmt(Math.max(...millis))}`);
  } else {
    lines.push(`- Capture window: ${NOT_CAPTURED}`);
  }
  return lines.join('\n');
}

function headersObjToLines(headers) {
  try {
    return Object.entries(headers || {}).map(([k, v]) => `${k}: ${v}`).join('\n');
  } catch {
    return '';
  }
}

function buildReplay(entries, rowsByRequestId) {
  const lines = ['**Replay**', ''];
  for (const e of entries) {
    const full = fullRowFor(e, rowsByRequestId);
    if (!full?.url) continue;
    try {
      const headersText = headersObjToLines(full.requestHeaders);
      const bodyText = (typeof full.requestBody === 'string' && full.requestBody.length <= MAX_BODY_LEN)
        ? full.requestBody
        : '';
      const curl = buildCurl({ method: full.method, url: full.url, headersText, bodyText });
      lines.push('```bash', curl, '```');
      return lines.join('\n');
    } catch {
      // try the next entry
    }
  }
  lines.push(NOT_CAPTURED);
  return lines.join('\n');
}

function buildRunRef(runId, launch) {
  const lines = ['**Run ref**', ''];
  lines.push(`- Run: ${runId || NOT_CAPTURED}`);
  if (launch) {
    lines.push(`- Launch: ${launch?.id || NOT_CAPTURED} · Ticket: ${launch?.ticket || NOT_CAPTURED}`);
  }
  return lines.join('\n');
}

/**
 * buildFindingTicket({ check, result, app, runId, launch, rowsByRequestId,
 * suiteName }) -> markdown string. Never throws.
 */
export function buildFindingTicket(opts) {
  try {
    const { check, result, app, runId, launch, rowsByRequestId, suiteName } = opts || {};
    const c = check || {};
    const r = result || {};

    const evidence = buildEvidence(r);
    const parts = [
      buildTitle(c, r),
      '',
      buildSummary(r),
      '',
      buildSpecReference(c, r, suiteName),
      '',
      evidence.text,
      '',
      buildReproContext(app, launch, rowsByRequestId, evidence.entries),
      '',
      buildReplay(evidence.entries, rowsByRequestId),
      '',
      buildRunRef(runId, launch),
    ];
    return parts.join('\n');
  } catch {
    // Truly unexpected failure — still return a minimal, non-throwing ticket.
    return `### [finding]\n\n${NOT_CAPTURED}`;
  }
}

export default buildFindingTicket;
